// SectionStepsMobileComponent.js
import React from 'react';
import { Box, Typography } from '@mui/material';
import ScrollFadeWrapper from '../../ScrollFadeWrapper/ScrollFadeWrapper';
import Image from '../../Image/Image';

// Cloudflare R2 vector
const SECTION_STEPS_VECTOR_URL =
  'https://assets.askhippo.co.za/Vectors/SectionStepsVector.svg';

const TITLE_TO_SUBTITLE_GAP = 16;
const SUBTITLE_TO_CARDS_GAP = 40;
const MOBILE_CARD_GAP = 32;
const FOOTER_TOP_GAP = 40;

const SectionStepsMobileComponent = ({ data, color }) => {
  if (!data) return null;
  const { title, subtitle, steps = [], footer } = data;
  const hasFooter = Boolean(String(footer || '').trim());

  return (
    <Box
      sx={{
        display: { xs: 'block', md: 'none' },
        width: '100%',
      }}
    >
      {/* Green box */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          backgroundColor: color === 'blue' ? '#162F3B' : '#C7CA00',
          borderRadius: '28px',
          px: '20px',
          py: '32px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          overflow: 'visible',
          zIndex: 1,
        }}
      >
        {/* Title */}
        <Typography
          component={'h3'}
          sx={{
            textAlign: 'center',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            fontSize: '2rem',
            lineHeight: '2.375rem',
            letterSpacing: '-0.03em',

            color: color === 'blue' ? '#FFFFFF' : '#162F3B',
          }}
        >
          {title}
        </Typography>

        {/* Description */}
        <Typography
          component={'p'}
          sx={{
            mt: `${TITLE_TO_SUBTITLE_GAP}px`,
            textAlign: 'center',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            fontSize: '1.125rem',
            lineHeight: '1.5rem',
            letterSpacing: '-0.03em',
            color: color === 'blue' ? '#FFFFFF' : '#162F3B',
            maxWidth: 520,
            mx: 'auto',
          }}
        >
          {subtitle}
        </Typography>

        {/* Cards section */}
        <Box
          sx={{
            mt: `${SUBTITLE_TO_CARDS_GAP}px`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: `${MOBILE_CARD_GAP}px`,
            width: '100%',
            zIndex: 3,
          }}
        >
          {steps.map((step, i) => {
            const hasNumber =
              step.number !== undefined &&
              step.number !== null &&
              step.number !== '';

            return (
              <ScrollFadeWrapper key={i} delay={i * 0.15}>
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    maxWidth: '480px',
                    backgroundColor: '#FFFFFF',
                    borderRadius: '24px',
                    pt: '72px',
                    px: '20px',
                    pb: '24px',
                    boxShadow:
                      '0px 1px 2px rgba(162,172,177,0.20), 0px 12px 24px -4px rgba(22,47,59,0.12)',
                  }}
                >
                  {/* Badge: number by default, icon when the config omits number */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '-16px',
                      left: '14px',
                      width: '72px',
                      height: '72px',
                      zIndex: 5,
                    }}
                  >
                    <Image
                      width={72}
                      height={72}
                      src={SECTION_STEPS_VECTOR_URL}
                      alt="Steps process graphic"
                      sx={{ width: '100%', height: '100%' }}
                    />

                    {hasNumber ? (
                      <Typography
                        component="p"
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          display: 'grid',
                          placeItems: 'center',
                          fontFamily: 'Pally, Inter, sans-serif',
                          fontWeight: 700,
                          fontSize: '3rem',
                          lineHeight: '3rem',
                          letterSpacing: '-0.03em',
                          color: '#FFFFFF',
                        }}
                      >
                        {step.number}
                      </Typography>
                    ) : step.icon ? (
                      <Image
                        width={36}
                        height={36}
                        src={step.icon}
                        alt={step.imgAltTag || step.heading}
                        title={step.imageAltTitle || step.heading}
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          width: '36px',
                          height: '36px',
                          objectFit: 'contain',
                          filter: 'brightness(0) invert(1)',
                        }}
                      />
                    ) : null}
                  </Box>

                  {/* Card text */}
                  <Typography
                    component={'h3'}
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 700,
                      fontSize: '1.375rem',
                      lineHeight: '1.75rem',
                      color: '#162F3B',
                      mb: '12px',
                      whiteSpace: 'pre-line',
                    }}
                  >
                    {step.heading}
                  </Typography>

                  <Typography
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 400,
                      fontSize: '0.9375rem',
                      lineHeight: '1.5rem',
                      color: '#162F3B',
                    }}
                  >
                    {step.text}
                  </Typography>
                </Box>
              </ScrollFadeWrapper>
            );
          })}
        </Box>

        {hasFooter && (
          <Box
            sx={{
              mt: `${FOOTER_TOP_GAP}px`,
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Typography
              component="p"
              sx={{
                fontFamily: '"Body/Body/Font Family", Inter, sans-serif',
                fontWeight: 400,
                fontSize: '1rem',
                lineHeight: '1.5rem',
                letterSpacing: '-0.002em',
                textAlign: 'center',
                color: '#162F3B',
                maxWidth: 520,
                mx: 'auto',
                whiteSpace: 'pre-line',
              }}
            >
              {footer}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SectionStepsMobileComponent;
